import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Search, UserPlus, Eye, Plus, Trash2 } from 'lucide-react';
import { formatCPF, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const Pacientes = () => {
  const { api, can } = useAuth();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('nome_asc'); // P2: Sorting state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pacienteToDelete, setPacienteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPacientes();
  }, []);

  const fetchPacientes = async () => {
    try {
      const response = await api.get('/pacientes');
      setPacientes(response.data);
    } catch (error) {
      console.error('Error fetching pacientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (paciente) => {
    setPacienteToDelete(paciente);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pacienteToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/pacientes/${pacienteToDelete.id}`);
      toast.success(`Paciente ${pacienteToDelete.nome} excluído com sucesso!`);
      fetchPacientes();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir paciente';
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPacienteToDelete(null);
    }
  };

  // P2: Filtered and sorted patients
  const filteredPacientes = useMemo(() => {
    let result = pacientes.filter((p) =>
      p.nome?.toLowerCase().includes(search.toLowerCase()) ||
      p.cpf?.includes(search.replace(/\D/g, ''))
    );
    
    // Sort results
    const sorted = [...result];
    switch (sortBy) {
      case 'nome_asc':
        return sorted.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      case 'nome_desc':
        return sorted.sort((a, b) => (b.nome || '').localeCompare(a.nome || '', 'pt-BR'));
      case 'cpf_asc':
        return sorted.sort((a, b) => (a.cpf || '').localeCompare(b.cpf || ''));
      case 'cpf_desc':
        return sorted.sort((a, b) => (b.cpf || '').localeCompare(a.cpf || ''));
      case 'data_asc':
        return sorted.sort((a, b) => new Date(a.criado_em || 0) - new Date(b.criado_em || 0));
      case 'data_desc':
        return sorted.sort((a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0));
      default:
        return sorted;
    }
  }, [pacientes, search, sortBy]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="pacientes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Pacientes</h1>
            <p className="text-muted-foreground mt-1">
              {filteredPacientes.length} paciente(s) cadastrado(s)
            </p>
          </div>
          {can('criar_pacientes') && (
          <Link to="/pacientes/novo">
            <Button className="gap-2" data-testid="btn-novo-paciente">
              <UserPlus className="h-4 w-4" />
              Novo Paciente
            </Button>
          </Link>
          )}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou CPF..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-pacientes"
                />
              </div>
              {/* P2: Sorting dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-44" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nome_asc">Nome (A–Z)</SelectItem>
                  <SelectItem value="nome_desc">Nome (Z–A)</SelectItem>
                  <SelectItem value="cpf_asc">CPF (Crescente)</SelectItem>
                  <SelectItem value="cpf_desc">CPF (Decrescente)</SelectItem>
                  <SelectItem value="data_desc">Data (Recente)</SelectItem>
                  <SelectItem value="data_asc">Data (Antiga)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Data Nascimento</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Cadastrado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      </TableRow>
                    ))
                  ) : filteredPacientes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nenhum paciente encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPacientes.map((paciente) => (
                      <TableRow key={paciente.id} data-testid={`paciente-row-${paciente.id}`}>
                        <TableCell className="font-medium">{paciente.nome}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {paciente.cpf_mascarado || formatCPF(paciente.cpf)}
                        </TableCell>
                        <TableCell>{formatDate(paciente.data_nascimento)}</TableCell>
                        <TableCell>{paciente.telefone || '-'}</TableCell>
                        <TableCell>{formatDate(paciente.criado_em)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Link to={`/pacientes/${paciente.id}`}>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                data-testid={`btn-ver-paciente-${paciente.id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Ver
                              </Button>
                            </Link>
                            {can('criar_internacoes') && (
                            <Link to={`/pacientes/${paciente.id}/nova-internacao`}>
                              <Button 
                                variant="outline" 
                                size="sm"
                                data-testid={`btn-nova-internacao-${paciente.id}`}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Internação
                              </Button>
                            </Link>
                            )}
                            {can('excluir_pacientes') && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(paciente)}
                              data-testid={`btn-excluir-paciente-${paciente.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Paciente</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o paciente <strong>{pacienteToDelete?.nome}</strong>?
                <br /><br />
                <span className="text-red-600 font-medium">
                  ⚠️ Esta ação é irreversível e excluirá também:
                </span>
                <ul className="list-disc list-inside mt-2 text-sm">
                  <li>Todas as internações do paciente</li>
                  <li>Todas as tarefas associadas</li>
                  <li>Todos os planejamentos terapêuticos</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Excluindo...' : 'Excluir Paciente'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Pacientes;
